import React, { useState, useMemo } from "react";
import classes from './PhGrid.module.scss';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";

export function usePagination() {
  const [ paginationOffset, setPaginationOffset ] = useState(0);
  const [ paginationSize, setPaginationSize ] = useState(10);
  const [ filterString, setFilterString ] = useState('');
  const [ sortColumn, setSortColumn ] = useState('');
  const [ sortKey, setSortKey ] = useState(null);
  const [ sortDirection, setSortDirection ] = useState('ascending');
  
  return {
    paginationOffset, setPaginationOffset,
    paginationSize, setPaginationSize,
    filterString, setFilterString,
    sortColumn, setSortColumn,
    sortKey, setSortKey,
    sortDirection, setSortDirection
  };
}
function getPaginationPages(currentPage, totalPages) {
  const pages = [];

  if (totalPages <= 7) {
    // Small page count → show all
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }

  pages.push(1); // always first

  if (currentPage > 4) {
    pages.push("..."); // first dot
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (currentPage < totalPages - 3) {
    pages.push("..."); // 2nd dot
  }

  pages.push(totalPages); // total pages merge
  return pages;
}

export function PhGridPagination({
  fullList,
  filteredListItems,
  paginationOffset,
  setPaginationOffset,
  setPaginationSize,
  paginationSize,
  filterString
}) {
  
  const paginatorSlots = useMemo(() => {
    
    const filteredList = filteredListItems ?? fullList.filter(row => Object.values(row).join('').includes(filterString));
    const totalPages = filteredList.length > 0 ? Math.ceil(filteredList.length / paginationSize) : 1;
    const currentPage = (paginationOffset + paginationSize) / paginationSize;
    return {
      filteredLength: filteredList.length,
      first: {
        handler: () => setPaginationOffset(0),
        disabled: currentPage === 1
      },
      previous: {
        handler: () => setPaginationOffset(paginationOffset - paginationSize),
        disabled: currentPage === 1
      },
      totalItems: filteredList.length,
      totalPages,
      currentPage,
      pages: getPaginationPages(currentPage, totalPages),
      previousDisabled: currentPage === 1,
      nextDisabled: currentPage === totalPages,
      next: {
        handler: () => setPaginationOffset(paginationOffset + paginationSize),
        disabled: currentPage === totalPages
      }
    };
  }, [
    filteredListItems,
    fullList,
    paginationOffset,
    setPaginationOffset,
    paginationSize,
    filterString
  ]);
  
  return (
    <>
      <div className="d-flex align-items-center justify-content-between px-2 py-2">
        {/* LEFT: Showing text */}
        <div className={classes["show-records-text"]}>
          Showing{" "}
          <span className={classes["bold-number"]}>{paginatorSlots.filteredLength === 0 ? 0 : paginationOffset + 1}</span> to{" "}
          <span className={classes["bold-number"]}> {Math.min(
            paginationOffset + paginationSize,
            paginatorSlots.filteredLength
          )}</span>
         {" "}
          of <span className={classes["bold-number"]}>{paginatorSlots.filteredLength}</span> records
        </div>

        {/* RIGHT: Page size + pagination */}
        <div className={`${classes["right-section"]} d-flex align-items-center gap-3`}>
          {/* Show per page */}
          <div className={classes["show-per-page"]}>
            <span>Show per Page</span>
            <select
              className="form-select form-select-sm"
              value={paginationSize}
              onChange={e => {
                setPaginationOffset(0);
                setPaginationSize(Number(e.target.value));
              }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          {/* Pagination */}
          <ul
            className={`${classes["pagination-btn"]} pagination pagination-sm mb-0 align-items-center`}
          >
            <li
              className={`page-item ${classes["previous-btn"]} ${
                paginatorSlots.previous.disabled ? classes["disabled"] : ""
              }`}
            >
              <button
                className="page-link border-0 bg-transparent text-muted"
                onClick={paginatorSlots.previous.handler}
                disabled={paginatorSlots.previous.disabled}
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Previous
              </button>
            </li>

            {/* Page numbers */}
            {paginatorSlots.pages.map((page, index) => (
              <li
                key={index}
                className={`${classes["page-item"]} ${
                  page === paginatorSlots.currentPage ? classes["active"] : ""
                } ${page === "..." ? classes["disabled"] : ""}`}
              >
                {page === "..." ? (
                  <span className={classes["page-link"]}>…</span>
                ) : (
                  <button
                    className={classes["page-link"]}
                    onClick={() =>
                      setPaginationOffset((page - 1) * paginationSize)
                    }
                  >
                    {page}
                  </button>
                )}
              </li>
            ))}

            <li
              className={`page-item  ${classes["next-btn"]} ${
                paginatorSlots.next.disabled ? classes["disabled"] : ""
              }`}
            >
              <button
                className="page-link border-0 bg-transparent text-muted"
                onClick={paginatorSlots.next.handler}
                disabled={paginatorSlots.next.disabled}
              >
                Next <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

export function PhGridFilter({
  placeholder,
  filterString,
  setFilterString,
  setPaginationOffset
}) {
  
  const handleClear = () => {
    setPaginationOffset(0);
    setFilterString("");
  };


  return (
    <div className={`form-group ${classes["search-filter-grid"]}`}>
      <input type="text" className="form-control" placeholder={placeholder || "Filter..."} value={filterString} onChange={(e) => {
        setPaginationOffset(0);
        setFilterString(e.target.value);
      }} />
      
    {filterString ? (
        <span className={`${classes["clear"]} ${classes["ph-filter-icon"]}`} onClick={handleClear}>
            <FontAwesomeIcon icon={faXmark} />
        </span>
      ) : (
        <span className={classes["ph-filter-icon"]}><FontAwesomeIcon icon={faMagnifyingGlass} /></span>
      )}

    </div>
  );
}

export function PhGridSort({
  columnLabel,
  columnName,
  config: {sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn}
}) {
  
  return (<span
    style={{whiteSpace: 'nowrap'}}
    onClick={() => {
      if (sortColumn === columnLabel) {// same column click
        setSortDirection(sortDirection === 'descending' ? 'ascending': 'descending');
      } else {// new column click
        setSortColumn(columnLabel);
        setSortDirection('descending');
        setSortKey(() => columnName);
      }
      
    }}
  >
    <span>{columnLabel}</span>
    <span className="ml-1 mr-3" style={{position: 'relative'}}>
      { (sortColumn === columnLabel && sortDirection === 'ascending')
        ? (
          <svg style={{position: 'absolute', top: '-1px'}} xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" className="bi bi-caret-up-fill" viewBox="0 0 14 14">
            <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z"/>
          </svg>
        )
        : (
          <svg style={{position: 'absolute', top: '-1px'}} xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="lightgray" className="bi bi-caret-up" viewBox="0 0 14 14">
            <path d="M3.204 11h9.592L8 5.519 3.204 11zm-.753-.659 4.796-5.48a1 1 0 0 1 1.506 0l4.796 5.48c.566.647.106 1.659-.753 1.659H3.204a1 1 0 0 1-.753-1.659z"/>
          </svg>
        )}
      { (sortColumn === columnLabel && sortDirection === 'descending')
        ? (
        <svg style={{position: 'absolute', bottom: '-1px'}} xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" className="bi bi-caret-down-fill" viewBox="0 0 14 14">
          <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
        </svg>
        )
        : (
        <svg style={{position: 'absolute', bottom: '-1px'}} xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="lightgray" className="bi bi-caret-down" viewBox="0 0 14 14">
          <path d="M3.204 5h9.592L8 10.481 3.204 5zm-.753.659 4.796 5.48a1 1 0 0 0 1.506 0l4.796-5.48c.566-.647.106-1.659-.753-1.659H3.204a1 1 0 0 0-.753 1.659z"/>
        </svg>
        )}
    </span>
  </span>);
}